import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";
import { Lock, Fingerprint, Shield, CheckCircle } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_social-circle-37/artifacts/vzzvahrb_IMG-20251227-WA0000.jpg";

const PinSetupPage = () => {
  const [step, setStep] = useState("create"); // create | confirm | biometric
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if Web Authentication API is available
    if (window.PublicKeyCredential) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then((available) => {
          setBiometricSupported(available);
        })
        .catch(() => {
          setBiometricSupported(false);
        });
    }
  }, []);

  const handleCreatePin = (value) => {
    setPin(value);
    if (value.length === 6) {
      setStep("confirm");
    }
  };

  const handleConfirmPin = async (value) => {
    setConfirmPin(value);
    if (value.length !== 6) return;

    if (value !== pin) {
      toast.error("PINs do not match. Please try again.");
      setPin("");
      setConfirmPin("");
      setStep("create");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/auth/pin/set", { pin: value });
      updateUser(response.data.user);
      toast.success("PIN set successfully!");
      
      // Show biometric setup option if supported
      if (biometricSupported) {
        setStep("biometric");
      } else {
        navigateToApp();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set PIN");
      setPin("");
      setConfirmPin("");
      setStep("create");
    } finally {
      setLoading(false);
    }
  };

  const handleSetupBiometric = async () => {
    setLoading(true);
    try {
      // Create a credential for WebAuthn
      const publicKeyCredentialCreationOptions = {
        challenge: new Uint8Array(32),
        rp: {
          name: "LUNAWAT FOUNDATION",
          id: window.location.hostname,
        },
        user: {
          id: new TextEncoder().encode(user.id),
          name: user.phone,
          displayName: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.phone,
        },
        pubKeyCredParams: [
          { alg: -7, type: "public-key" },
          { alg: -257, type: "public-key" },
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required",
        },
        timeout: 60000,
        attestation: "none",
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      });

      if (credential) {
        // Register biometric on server
        const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
        const response = await api.post("/auth/biometric/register", { credential_id: credentialId });
        updateUser(response.data.user);
        toast.success("Biometric authentication enabled!");
      }
      
      navigateToApp();
    } catch (error) {
      console.error("Biometric setup error:", error);
      toast.error("Failed to set up biometric. You can enable it later in settings.");
      navigateToApp();
    } finally {
      setLoading(false);
    }
  };

  const navigateToApp = () => {
    if (!user.is_profile_complete) {
      navigate("/onboarding");
    } else if (user.verification_status !== "verified") {
      navigate("/pending-verification");
    } else {
      navigate("/welcome");
    }
  };

  const skipBiometric = () => {
    toast.info("You can enable biometric authentication later in settings.");
    navigateToApp();
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="pin-setup-page">
      {/* Watermark */}
      <div className="fixed inset-0 pointer-events-none z-0 flex items-center justify-center">
        <img 
          src={LOGO_URL} 
          alt="Lunawat Foundation" 
          className="w-64 h-64 md:w-96 md:h-96 object-contain opacity-5"
        />
      </div>

      <Card className="w-full max-w-md border-0 shadow-xl animate-fade-in relative z-10" data-testid="pin-setup-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              {step === "biometric" ? (
                <Fingerprint className="w-8 h-8 text-primary" />
              ) : (
                <Lock className="w-8 h-8 text-primary" />
              )}
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">
            {step === "create" && "Create Your PIN"}
            {step === "confirm" && "Confirm Your PIN"}
            {step === "biometric" && "Enable Biometric Login"}
          </CardTitle>
          <CardDescription>
            {step === "create" && "Set up a 6-digit PIN for quick access"}
            {step === "confirm" && "Enter your PIN again to confirm"}
            {step === "biometric" && "Use fingerprint or face unlock for faster login"}
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6">
          {step === "create" && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={pin}
                  onChange={handleCreatePin}
                  type="password"
                  data-testid="create-pin-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="w-12 h-14 text-xl" />
                    <InputOTPSlot index={1} className="w-12 h-14 text-xl" />
                    <InputOTPSlot index={2} className="w-12 h-14 text-xl" />
                    <InputOTPSlot index={3} className="w-12 h-14 text-xl" />
                    <InputOTPSlot index={4} className="w-12 h-14 text-xl" />
                    <InputOTPSlot index={5} className="w-12 h-14 text-xl" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  <span>Your PIN is securely encrypted</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span>Use it for quick login instead of OTP</span>
                </div>
              </div>
            </div>
          )}

          {step === "confirm" && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={confirmPin}
                  onChange={handleConfirmPin}
                  disabled={loading}
                  type="password"
                  data-testid="confirm-pin-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="w-12 h-14 text-xl" />
                    <InputOTPSlot index={1} className="w-12 h-14 text-xl" />
                    <InputOTPSlot index={2} className="w-12 h-14 text-xl" />
                    <InputOTPSlot index={3} className="w-12 h-14 text-xl" />
                    <InputOTPSlot index={4} className="w-12 h-14 text-xl" />
                    <InputOTPSlot index={5} className="w-12 h-14 text-xl" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {loading && (
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
                </div>
              )}

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setStep("create");
                  setPin("");
                  setConfirmPin("");
                }}
                disabled={loading}
              >
                Back
              </Button>
            </div>
          )}

          {step === "biometric" && (
            <div className="space-y-6">
              <div className="bg-primary/5 rounded-lg p-6 text-center">
                <Fingerprint className="w-16 h-16 text-primary mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                  Enable fingerprint or face recognition for instant access to your account
                </p>
              </div>

              <div className="space-y-3">
                <Button 
                  className="w-full h-12"
                  onClick={handleSetupBiometric}
                  disabled={loading}
                  data-testid="enable-biometric-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary-foreground" />
                  ) : (
                    <>
                      <Fingerprint className="w-4 h-4 mr-2" />
                      Enable Biometric
                    </>
                  )}
                </Button>

                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={skipBiometric}
                  disabled={loading}
                >
                  Skip for Now
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PinSetupPage;
